import React, { useEffect, useState, useRef } from 'react';
import { useChatBar, ChatWindow as ChatWindowType } from '../../context/ChatBarContext';
import { useAuth } from '../../context/AuthContext';
import { X, Minus, MessageCircle, Maximize2 } from 'lucide-react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, doc, Timestamp, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase/config';
import MessageInput from '../finChats/finChatsInput';
import MessageBubble from '../finChats/finChatsBubble';
import { toast } from 'react-hot-toast';
import { ClaireGuideButton } from '../claire/ClaireGuideButton';
import { isClaireEmail } from '../../lib/claire/guide';

interface Message {
  id: string;
  sender: string;
  text: string;
  imageUrl?: string;
  attachment?: {
    url: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    uploadPath: string;
  };
  timestamp: Timestamp;
  editedAt?: Timestamp;
  isEdited?: boolean;
}

interface ChatWindowProps {
  chat: ChatWindowType;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ chat }) => {
  const { closeChat, toggleMinimize, markChatAsRead } = useChatBar();
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [userDisplayNames, setUserDisplayNames] = useState<Map<string, string>>(new Map());
  const [userPhotoURLs, setUserPhotoURLs] = useState<Map<string, string>>(new Map());

  const chatId = chat.chatId;
  const displayName = chat.chatData?.displayName || chat.chatData?.name || 'Chat';
  const photoURL = chat.chatData?.photoURL || null;
  const isGroup = chat.chatData?.isGroup || false;

  // Add current user to display maps
  useEffect(() => {
    if (currentUser?.email) {
      setUserDisplayNames(prev => new Map(prev).set(currentUser.email!, currentUser.displayName || currentUser.email!));
      setUserPhotoURLs(prev => new Map(prev).set(currentUser.email!, currentUser.photoURL || ''));
    }
  }, [currentUser]);

  // Function to fetch user data
  const fetchUserData = async (email: string) => {
    if (userDisplayNames.has(email)) return;

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        setUserDisplayNames(prev => new Map(prev).set(email, userData.displayName || email));
        setUserPhotoURLs(prev => new Map(prev).set(email, userData.photoURL || ''));
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const getUserDisplayName = (email: string): string => {
    return userDisplayNames.get(email) || email;
  };

  const getUserPhotoURL = (email: string): string | undefined => {
    return userPhotoURLs.get(email);
  };

  // Firebase subscription for messages
  useEffect(() => {
    if (!chatId) return;

    setLoading(true);

    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const messagesQuery = query(messagesRef, orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Message));

      setMessages(msgs);
      setLoading(false);

      // Fetch user data for all unique senders
      const uniqueSenders = Array.from(new Set(msgs.map(m => m.sender)));
      uniqueSenders.forEach(sender => {
        if (sender && sender !== currentUser?.email) {
          fetchUserData(sender);
        }
      });

      // Update unread count when minimized
      if (chat.isMinimized && msgs.length > lastMessageCount) {
        const newMessageCount = msgs.length - lastMessageCount;
        const newMessagesFromOthers = msgs.slice(-newMessageCount).filter(
          m => m.sender !== currentUser?.email
        ).length;
        
        if (newMessagesFromOthers > 0) {
          // Unread count is already tracked in context
        }
      }
      
      setLastMessageCount(msgs.length);
    });

    return unsubscribe;
  }, [chatId]);

  // Auto-scroll to bottom on initial load (immediate)
  useEffect(() => {
    if (!chat.isMinimized && messages.length > 0) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'auto' });
      });
    }
  }, [chat.isMinimized]);

  // Auto-scroll to bottom when new messages arrive (smooth)
  useEffect(() => {
    if (!chat.isMinimized && messages.length > 0) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  }, [messages.length]);

  // Mark as read when expanded
  useEffect(() => {
    if (!chat.isMinimized) {
      markChatAsRead(chatId);
    }
  }, [chat.isMinimized, chatId, markChatAsRead]);

  const sendMessage = async (text: string, imageUrl?: string, attachment?: any) => {
    if (!currentUser?.email || (!text.trim() && !imageUrl && !attachment)) return;

    try {
      const message = {
        sender: currentUser.email,
        text: text.trim(),
        ...(imageUrl && { imageUrl }),
        ...(attachment && { attachment }),
        timestamp: Timestamp.now()
      };

      await addDoc(collection(db, 'chats', chatId, 'messages'), message);

      // Update parent chat document
      const chatRef = doc(db, 'chats', chatId);
      let lastMessage = text.trim();
      if (imageUrl && !text.trim()) {
        lastMessage = '📷 Image';
      } else if (attachment && !text.trim()) {
        lastMessage = `📎 ${attachment.fileName}`;
      } else if (attachment && text.trim()) {
        lastMessage = `📎 ${attachment.fileName}: ${text.trim()}`;
      }

      await updateDoc(chatRef, {
        lastMessage,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const editMessage = async (messageId: string, newText: string) => {
    if (!currentUser?.email || !newText.trim()) return;

    try {
      const messageRef = doc(db, 'chats', chatId, 'messages', messageId);
      await updateDoc(messageRef, {
        text: newText.trim(),
        editedAt: Timestamp.now(),
        isEdited: true
      });

      toast.success('Message updated');
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error('Failed to edit message');
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden transition-all duration-300"
      style={{
        width: '320px',
        height: chat.isMinimized ? '56px' : '450px',
      }}
    >
      {/* Header - Light Mode */}
      <div
        className={`px-3 py-2.5 border-b border-gray-200 flex items-center gap-2 bg-gradient-to-r from-gray-50 to-gray-100 ${
          chat.isMinimized ? 'cursor-pointer hover:from-gray-100 hover:to-gray-150 transition-colors' : ''
        }`}
        onClick={chat.isMinimized ? () => toggleMinimize(chatId) : undefined}
      >
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          {isGroup ? (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center border border-gray-200">
              <MessageCircle className="w-4 h-4 text-indigo-600" />
            </div>
          ) : (
            <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200">
              {photoURL ? (
                <img
                  src={photoURL}
                  alt={displayName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                    if (nextElement) {
                      nextElement.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div
                className={`w-full h-full bg-gradient-to-br from-indigo-400 via-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm ${
                  photoURL ? 'hidden' : 'flex'
                }`}
              >
                {displayName.charAt(0).toUpperCase()}
              </div>
            </div>
          )}
          
          {/* Unread badge */}
          {chat.unreadCount > 0 && chat.isMinimized && (
            <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold px-1 animate-pulse shadow-lg">
              {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
            </div>
          )}
        </div>

        {/* Name */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 text-sm truncate">{displayName}</h3>
          {chat.isMinimized && chat.unreadCount > 0 && (
            <p className="text-xs text-red-500 truncate">
              {chat.unreadCount} new message{chat.unreadCount > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          {messages.some((m) => isClaireEmail(m.sender)) && <ClaireGuideButton />}
          <button
            onClick={() => toggleMinimize(chatId)}
            className="p-1.5 hover:bg-gray-200 rounded-md transition-colors text-gray-500 hover:text-gray-700"
            title={chat.isMinimized ? "Expand" : "Minimize"}
          >
            {chat.isMinimized ? (
              <Maximize2 className="w-3.5 h-3.5" />
            ) : (
              <Minus className="w-3.5 h-3.5" />
            )}
          </button>
          <button
            onClick={() => closeChat(chatId)}
            className="p-1.5 hover:bg-red-100 rounded-md transition-colors text-gray-500 hover:text-red-600"
            title="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Chat content - only show when expanded */}
      {!chat.isMinimized && (
        <div className="flex flex-col" style={{ height: 'calc(100% - 56px)' }}>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar bg-gray-50">
            {loading ? (
              <div className="flex items-center justify-center p-4">
                <div className="w-5 h-5 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin"></div>
                <span className="ml-2 text-gray-500 text-xs">Loading...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6 text-gray-400 text-xs text-center">
                <MessageCircle className="w-8 h-8 mb-2 opacity-30" />
                <p>No messages yet.</p>
                <p className="mt-1">Start the conversation!</p>
              </div>
            ) : (
              messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isOwn={message.sender === currentUser?.email}
                  senderDisplayName={getUserDisplayName(message.sender)}
                  senderPhotoURL={getUserPhotoURL(message.sender)}
                  currentUserEmail={currentUser?.email || ''}
                  onEdit={(messageId, newText) => editMessage(messageId, newText)}
                  compact={true}
                  chatId={chatId}
                />
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {/* Message Input */}
          <div className="border-t border-gray-200 bg-white">
            <MessageInput
              onSend={sendMessage}
              compact={true}
              chatId={chatId}
              currentUser={currentUser}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
